import React, { useState, useEffect } from "react";
import Select, { OptionsType, Styles } from "react-select";
import { Button } from "@pepdirect/ui/button";
import { ReactSelectDropdownOption } from "@pepdirect/ui/dropdown/types";
import { customReactSelectStyles } from "@pepdirect/ui/dropdown/constants";
import { DropdownIndicator } from "@pepdirect/ui/DropdownIndicator";
import { formatDropdownQty } from "@pepdirect/ui/dropdown/helpers";
import { range } from "helpers/range";
import st from "./quantity.module.scss";

const selectStyles: Partial<Styles<ReactSelectDropdownOption, false>> = {
  ...customReactSelectStyles,
  container: (provided) => ({
    ...provided,
    minWidth: "82px",
  }),
  singleValue: () => ({
    textAlign: "center",
    width: "100%",
  }),
};

function getOptions(
  quantity: number,
  inventoryQty: number,
  min: number,
  maxCartQty: number
): OptionsType<ReactSelectDropdownOption> {
  const max = Math.min(maxCartQty, inventoryQty);
  const options = range(0, max).map((num) => {
    return {
      value: num.toString(),
      label: num.toString(),
    };
  });

  /* TODO: rework this part? */
  if (quantity > max)
    options.push({
      value: quantity.toString(),
      label: `${quantity} – more than max, please select less`,
    });
  if (quantity < min && quantity !== 0)
    options.unshift({
      value: quantity.toString(),
      label: `${quantity} – less than min, please select more`,
    });

  return options;
}

// TODO: Work more with 1 variant? Need example of data
export function QuantityDropdown({
  quantity,
  onChange,
  outOfStock = false,
  inventoryQty = Infinity,
  minOrderQty,
  maxCartQty,
}: {
  quantity: number;
  onChange: (qty: number) => Promise<void>;
  outOfStock?: boolean;
  inventoryQty?: number;
  minOrderQty?: number;
  maxCartQty: number;
}): JSX.Element {
  const noInventory = outOfStock || !inventoryQty;
  const min = minOrderQty || 1;
  const [selectedQuantity, setSelectedQuantity] = useState(
    formatDropdownQty(min)
  );

  useEffect(() => {
    if (quantity > 0) setSelectedQuantity(formatDropdownQty(quantity));
  }, [quantity]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isInCart = quantity > 0;

  const onButtonPress = async (quantity: string) => {
    try {
      setLoading(true);
      await onChange(parseInt(quantity));
      setError("");
    } catch {
      setError("Could not add product");
    } finally {
      setLoading(false);
    }
  };

  const options = getOptions(quantity, inventoryQty, min, maxCartQty);

  return (
    <>
      <div className={st.label}>Quantity</div>
      <div className={st.wrapper}>
        <Select
          inputId="quantity"
          value={selectedQuantity}
          onChange={(newValue) =>
            setSelectedQuantity(newValue as ReactSelectDropdownOption)
          }
          options={options}
          styles={selectStyles}
          components={{ DropdownIndicator }}
          isDisabled={noInventory}
          isSearchable={false}
        />
        <div className={st.buttonContainer}>
          {noInventory ? (
            <Button disabled fullWidth>
              Out of stock
            </Button>
          ) : (
            <>
              <Button
                type="secondary"
                fullWidth
                onClick={() => onButtonPress(selectedQuantity.value)}
                loading={loading}
              >
                {isInCart ? "Update Cart" : "Add To Cart"}
              </Button>
              <span className={st.error}>{error}</span>
            </>
          )}
        </div>
      </div>
    </>
  );
}
